import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaHistory } from 'react-icons/fa';
import Select from 'react-select';
import paymentService from '../services/paymentService';
import currentAccountService from '../services/currentAccountService';
import bankService from '../services/bankService';
import api from '../services/api'; // Servis dosyasında eksik olma ihtimaline karşı api importu

const PaymentFormPage = () => {
  const [formData, setFormData] = useState({
    current_account_id: '',
    payment_type: 'tahsilat',
    amount: '',
    payment_method: 'nakit',
    bank_account_id: '',
    description: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accData, bankData] = await Promise.all([
          currentAccountService.getAll(),
          bankService.getAllAccounts()
        ]);
        setAccounts(accData);
        setBanks(bankData);

        if (id) {
          // Düzenleme modu: Veriyi çek (Serviste yoksa api kullan)
          const response = await api.get(`/payments/${id}`);
          const payment = response.data;
          
          setFormData({
            current_account_id: payment.current_account_id,
            payment_type: payment.payment_type,
            amount: payment.amount,
            payment_method: payment.payment_method,
            bank_account_id: payment.bank_account_id || '',
            description: payment.description,
            payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().split('T')[0] : ''
          });
        }

      } catch (error) {
        toast.error('Veriler yüklenirken hata oluştu.');
      }
    };
    loadData();
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.current_account_id) {
      toast.warning('Lütfen bir cari hesap seçiniz.');
      return;
    }

    try {
      // Banka seçildiyse ama banka hesabı seçilmediyse uyarı ver (Frontend validasyonu)
      if (formData.payment_method === 'banka' && !formData.bank_account_id) {
        toast.warning('Lütfen bir banka hesabı seçiniz.');
        return;
      }

      // Banka hesabı seçilmediyse null gönder (Backend Joi validasyonu için boş string yerine null)
      const dataToSend = {
        ...formData,
        bank_account_id: formData.bank_account_id || null
      };

      if (id) {
        await api.put(`/payments/${id}`, dataToSend);
        toast.success('İşlem güncellendi.');
      } else {
        await paymentService.create(dataToSend);
        toast.success('İşlem başarıyla kaydedildi.');
      }
      navigate('/payments');
    } catch (error) {
      toast.error(error.response?.data?.message || 'İşlem başarısız.');
    }
  };

  const accountOptions = accounts.map(acc => ({ value: acc.id, label: acc.company_name }));

  // Seçili cariyi bul (Bakiye gösterimi için)
  const selectedAccount = accounts.find(acc => acc.id === formData.current_account_id);

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center mb-3">
        <button onClick={() => navigate(-1)} className="btn btn-outline-secondary">
          <FaArrowLeft className="me-2" /> Geri
        </button>
      </div>

      <div className="card shadow-sm" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="card-header bg-white">
          <h4 className="mb-0">{id ? 'İşlem Düzenle' : 'Yeni Tahsilat / Ödeme'}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Cari Hesap</label>
                <Select
                  options={accountOptions}
                  value={accountOptions.find(opt => opt.value === formData.current_account_id)}
                  onChange={(option) => setFormData({ ...formData, current_account_id: option ? option.value : '' })}
                  placeholder="Seçiniz..."
                  isSearchable
                  isClearable
                  styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                />
                {selectedAccount && (
                  <div className="mt-2 p-2 bg-light border rounded d-flex justify-content-between align-items-center">
                    <div>
                      <span className="text-muted small me-2">Mevcut Bakiye:</span>
                      <span className={`fw-bold ${parseFloat(selectedAccount.balance) >= 0 ? 'text-dark' : 'text-danger'}`}>
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Math.abs(selectedAccount.balance || 0))}
                        <span className="small text-muted ms-1">
                          {parseFloat(selectedAccount.balance) > 0 
                            ? (selectedAccount.account_type === 'musteri' ? '(Borçlu)' : '(Alacaklı)') 
                            : parseFloat(selectedAccount.balance) < 0 
                              ? (selectedAccount.account_type === 'musteri' ? '(Alacaklı)' : '(Borçlu)') 
                              : '(Dengede)'}
                        </span>
                      </span>
                    </div>
                    <Link 
                      to={`/current-accounts/detail/${selectedAccount.id}`} 
                      target="_blank" 
                      className="btn btn-sm btn-outline-info text-nowrap"
                      title="Hareketleri yeni sekmede aç"
                    >
                      <FaHistory className="me-1" /> Hareketler
                    </Link>
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">İşlem Tipi</label>
                <select name="payment_type" className="form-select" value={formData.payment_type} onChange={handleChange}>
                  <option value="tahsilat">Tahsilat (Para Girişi)</option>
                  <option value="odeme">Ödeme (Para Çıkışı)</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Tutar (TL)</label>
                <input type="number" name="amount" className="form-control" value={formData.amount} onChange={handleChange} required min="0" step="0.01" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Tarih</label>
                <input type="date" name="payment_date" className="form-control" value={formData.payment_date} onChange={handleChange} required />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Ödeme Yöntemi</label>
                <select name="payment_method" className="form-select" value={formData.payment_method} onChange={handleChange}>
                  <option value="nakit">Nakit (Kasa)</option>
                  <option value="banka">Banka (Havale/EFT)</option>
                </select>
              </div>
              
              {formData.payment_method === 'banka' && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Banka Hesabı</label>
                  <select name="bank_account_id" className="form-select" value={formData.bank_account_id} onChange={handleChange} required>
                    <option value="">Seçiniz...</option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.id}>{bank.bank_name} - {bank.iban}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12 mb-3">
                <label className="form-label">Açıklama</label>
                <textarea name="description" className="form-control" rows="3" value={formData.description} onChange={handleChange}></textarea>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary me-2">İptal</button>
              <button type="submit" className="btn btn-primary">Kaydet</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentFormPage;